import AppContext from "PFApp/app_context";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import DropdownButton from "PFComponents/dropdown/dropdown_button";
import AutoSelect from "PFComponents/select/autoselect";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Tooltip from "PFComponents/tooltip/tooltip";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { ApiRoute } from "PFCore/utilities/routes";
import { CURRENT_PROFILE_ASYNC_ACTIONS } from "PFReducers/current_profile_reducer";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import css from "./edit_custom_field.less";

const EditCustomFieldPage = ({ customField, id }) => {
  const { dispatch } = useContext(AppContext);
  const { t } = useTranslation("admin", { keyPrefix: "customFields" });
  const { t: defaultT } = useTranslation("translation");
  const history = useHistory();
  const isNew = !id;
  const header = isNew ? t("titleCreate") : t("titleEdit");

  const [storedAs, setStoredAs] = useState(customField?.name || "");
  const [displayAs, setDisplayAs] = useState(customField?.display_as || "");
  const [description, setDescription] = useState(customField?.description || "");
  const [position, setPostion] = useState(isNew ? null : customField?.position);
  const [suggestionDescription, setSuggestionDescription] = useState(
    customField?.suggestion_description || ""
  );
  const [valueType, setValueType] = useState(customField?.value_type || "");
  const [kind, setkind] = useState(customField?.kind || "");
  const [newValues, setNewValues] = useState(customField?.new_values || "");
  const [profileCompletion, setProfileCompletion] = useState(
    isNew ? null : customField?.profile_completion_weight
  );
  const [sections, setSections] = useState(customField?.sections || []);
  const [errors, setErrors] = useState([]);

  const { getFormattedErrors } = useResponseErrors();

  const saveCustomField = () => {
    const payload = {
      ...customField,
      name: storedAs,
      display_as: displayAs,
      description: description,
      suggestion_description: suggestionDescription,
      value_type: valueType.toLowerCase(),
      kind: kind.toLowerCase(),
      new_values: newValues.toLowerCase(),
      profile_completion_weight: profileCompletion,
      // v1 can't handle arrays
      sections: sections.map(({ id }) => id).join("&#x2c;"),
      position
    };

    $.ajax({
      url: ApiRoute(`/api/custom_types/${id ? id : ""}`),
      method: id ? "PUT" : "POST",
      api_version: 2,
      data: payload
    })
      .then(() => {
        dispatch({
          type: CURRENT_PROFILE_ASYNC_ACTIONS.CURRENT_PROFILE_FETCH
        });
        history.push("/admin/custom_fields");
      })
      .catch((err) => {
        setErrors(getFormattedErrors(err));
      });
  };

  const dropDownRenderer = (options, value, handleChange, disabled, disabledTooltipContent) => {
    let hasError;
    if (Number(value) >= 0) {
      hasError = ![0, 1, 2, 3, 4].includes(value);
    } else {
      hasError = value?.length === 0 || typeof value !== "string";
    }

    return (
      <Tooltip content={disabledTooltipContent} disabled={!disabled} placement="top-start">
        <div className={css.dropDown}>
          <DropdownButton
            disabled={disabled}
            options={options}
            buttonKind="secondary"
            handleChange={handleChange}
            popperOptions={{
              placement: "bottom-start"
            }}
          >
            {options.find(({ item }) => item === value)?.displayElement}
          </DropdownButton>
          {hasError && <span className={css.error}>{`can't be blank`}</span>}
        </div>
      </Tooltip>
    );
  };

  const valueTypesOptions = [
    {
      id: "String",
      displayElement: <span>{t("valueTypeOptions.string")}</span>,
      item: "string"
    },
    {
      id: "Integer",
      displayElement: <span>{t("valueTypeOptions.integer")}</span>,
      item: "integer"
    },
    {
      id: "Date",
      displayElement: <span>{t("valueTypeOptions.date")}</span>,
      item: "date"
    },
    {
      id: "Location",
      displayElement: <span>{t("valueTypeOptions.location")}</span>,
      item: "location"
    }
  ];
  const kindOptions = [
    {
      id: "single",
      displayElement: <span>{t("kindOptions.single")}</span>,
      item: "single"
    },
    {
      id: "multiple",
      displayElement: <span>{t("kindOptions.multiple")}</span>,
      item: "multiple"
    }
  ];

  const newValuesOptions = [
    {
      id: "new_values_allowed",
      displayElement: <span>{t("newValuesAllowed")}</span>,
      item: "allowed"
    },
    {
      id: "new_values_not_allowed",
      displayElement: <span>{t("newValuesNotAllowed")}</span>,
      item: "not_allowed"
    },
    {
      id: "new_values_require_approval",
      displayElement: <span>{t("newValuesRequireApproval")}</span>,
      item: "require_approval"
    }
  ];

  const profileCompletionOptions = [
    {
      id: 0,
      displayElement: <span>0</span>,
      item: 0
    },
    {
      id: 1,
      displayElement: <span>1</span>,
      item: 1
    },
    {
      id: 2,
      displayElement: <span>2</span>,
      item: 2
    },
    {
      id: 3,
      displayElement: <span>3</span>,
      item: 3
    }
  ];

  const isHierarchical = customField?.orderable;

  return (
    <div className={css.wrapper}>
      <h1 className={css.header}>{header}</h1>
      <PageBox style={{ padding: 20 }}>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.storedAs")}</div>
          <div className={css.right}>
            {id ? (
              `${storedAs}`
            ) : (
              <InputFieldSet value={storedAs} onChange={setStoredAs} error={errors.name} />
            )}
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.displayAs")}</div>
          <div className={css.right}>
            <InputFieldSet value={displayAs} onChange={setDisplayAs} error={errors.display_as} />
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.description")}</div>
          <div className={css.right}>
            <InputFieldSet value={description} onChange={setDescription} error={errors.description} />
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.suggestionDescription")}</div>
          <div className={css.right}>
            <InputFieldSet value={suggestionDescription} onChange={setSuggestionDescription} />
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.position")}</div>
          <div className={css.right}>
            <InputFieldSet
              value={position}
              onChange={setPostion}
              inputType="number"
              min={0}
              error={errors.position}
            />
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.valueType")}</div>
          <div className={css.right}>
            {dropDownRenderer(valueTypesOptions, valueType, setValueType, !isNew)}
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.kind")}</div>

          <div className={css.right}>
            {dropDownRenderer(
              kindOptions,
              kind,
              setkind,
              isHierarchical,
              t("toSelectDifferentTurnOffHierarchical")
            )}
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("allowsNewValues")}</div>
          <div className={css.right}>
            {dropDownRenderer(
              newValuesOptions,
              newValues,
              setNewValues,
              isHierarchical,
              t("toSelectDifferentTurnOffHierarchical")
            )}
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("editFields.sections")}</div>
          <div className={css.right}>
            <AutoSelect
              label=""
              multi={true}
              values={sections}
              closeOnChange
              error={errors.sections}
              query={(term) =>
                $.ajax({
                  url: ApiRoute("/api/autocomplete/sections"),
                  api_version: 1,
                  data: { term }
                })
              }
              filterOptions={(res) => {
                const sectionsIds = sections?.map((item) => item.id);
                return res.filter((item) => !sectionsIds.includes(item.id));
              }}
              parseResponse={(response) => response}
              formatOption={(item) => ({
                id: item.id,
                displayElement: <span>{item.text}</span>,
                item: item
              })}
              handleChange={setSections}
            />
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("profileCompletionWeight")}</div>
          <div className={css.right}>
            {dropDownRenderer(profileCompletionOptions, profileCompletion, setProfileCompletion, false)}
          </div>
        </div>
        <div className={css.footer}>
          <ButtonLink kind="secondary" href="/admin/custom_fields">
            {defaultT("cancel")}
          </ButtonLink>
          <Button text={id ? defaultT("update") : defaultT("create")} onClick={saveCustomField} />
        </div>
      </PageBox>
    </div>
  );
};

EditCustomFieldPage.propTypes = {
  customField: PropTypes.shape({
    name: PropTypes.string,
    display_as: PropTypes.string,
    description: PropTypes.string,
    suggestion_description: PropTypes.string,
    value_type: PropTypes.string,
    kind: PropTypes.string,
    new_values: PropTypes.string,
    profile_completion_weight: PropTypes.number,
    sections: PropTypes.array,
    position: PropTypes.number,
    orderable: PropTypes.bool
  }),
  id: PropTypes.number
};

export default EditCustomFieldPage;
